import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { fetchClientPrincipal } from '../api/auth.js';
import { clearLoginRedirectGuard } from '../api/client.js';
import { AUTH_DISABLED } from '../constants/env.js';
import { Permission, Role, hasPermission, resolveRole, rolePermissions } from './roles.js';

// AuthProvider resolves the signed-in user once on mount, exposes the
// resolved role + permission set via context, and triggers a redirect to
// the SWA login URL when no principal is returned.
//
// Statuses surfaced via context:
//   loading         — /.auth/me request in flight
//   authenticated   — principal resolved, role is system_admin / billing_access / view_only
//   unauthorized    — principal exists but in no recognized group (unknown)
//   unauthenticated — no principal returned; user not signed in
//   error           — fetch failed (network etc.); UI shows a retry banner
//
// SWA Standard with linked-backend means this code runs in the browser
// while signed in via the platform — no MSAL.js, no Bearer tokens. The
// backend is reached through relative /api/* paths and SWA forwards a
// signed x-ms-client-principal header on each call.

const AuthContext = createContext(null);

const _GROUP_CLAIM_TYPES = new Set([
  'groups',
  // Microsoft is inconsistent across edge versions; accept long form too.
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
]);

function _extractGroupIds(principal) {
  if (!principal) return [];
  // Primary path: principal.claims is an array of {typ, val}; one entry per group.
  if (Array.isArray(principal.claims)) {
    const fromClaims = principal.claims
      .filter((c) => c && _GROUP_CLAIM_TYPES.has(c.typ))
      .map((c) => c.val)
      .filter(Boolean);
    if (fromClaims.length > 0) return fromClaims;
  }
  // Fallback: SWA can also project group IDs into userRoles when SWA
  // role-management maps them. Treat values that look like GUIDs as group IDs.
  if (Array.isArray(principal.userRoles)) {
    const guidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return principal.userRoles.filter((r) => guidLike.test(r));
  }
  return [];
}

export function AuthProvider({ children }) {
  const [status, setStatus] = useState('loading');
  const [principal, setPrincipal] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    // Non-prod escape hatch: skip the SWA sign-in entirely and run as a
    // synthetic system_admin. Matches the backend (ENTRA_AUTH_ENABLED=false),
    // which also resolves every request to system_admin in this mode.
    if (AUTH_DISABLED) {
      setPrincipal({
        userId: 'auth-disabled',
        userName: 'Auth disabled (test)',
        provider: 'none',
        groups: [],
        role: Role.SYSTEM_ADMIN,
      });
      setStatus('authenticated');
      return () => { cancelled = true; };
    }

    (async () => {
      try {
        const cp = await fetchClientPrincipal();
        if (cancelled) return;

        if (!cp) {
          setStatus('unauthenticated');
          setPrincipal(null);
          return;
        }

        // Authentication confirmed via SWA — clear the one-shot login
        // redirect guard so a later session expiry can redirect again.
        clearLoginRedirectGuard();

        const groups = _extractGroupIds(cp);
        // Frontend role resolution from /.auth/me's claims is best-effort
        // for UI gating only. The backend independently resolves role via
        // Graph and is the authoritative permission check.
        const role = resolveRole(groups);

        setPrincipal({
          userId:    cp.userId || '',
          userName:  cp.userDetails || '',
          provider:  cp.identityProvider || 'aad',
          groups,
          role,
        });
        setStatus(role === Role.UNKNOWN ? 'unauthorized' : 'authenticated');
      } catch (e) {
        if (cancelled) return;
        setError(e);
        setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => {
    const role = principal?.role || Role.UNAUTHENTICATED;
    return {
      status,
      error,
      principal,
      role,
      permissions: rolePermissions(role),
      hasPermission: (perm) => hasPermission(role, perm),
      // For convenience on action-button components.
      isAdmin:        role === Role.SYSTEM_ADMIN,
      isBillingAccess: role === Role.BILLING_ACCESS,
      isViewOnly:     role === Role.VIEW_ONLY,
    };
  }, [status, error, principal]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function useHasPermission(permission) {
  return useAuth().hasPermission(permission);
}

export function useRole() {
  return useAuth().role;
}

// Re-export for convenience so callers can do
//   import { Permission, useAuth } from './auth/AuthProvider';
export { Permission, Role };
